// Direct Upload Service - Bunny Stream Direct Upload
import api from '../config/axios';
import * as tus from 'tus-js-client';

class DirectUploadService {
  /**
   * Get direct upload URL from backend
   * @param {Object} lessonData - Lesson data
   * @param {File} thumbnailFile - Optional thumbnail file
   * @returns {Promise<Object>} Upload info (now includes TUS credentials)
   */
  async getUploadUrl(lessonData, thumbnailFile = null) {
    try {
      const formData = new FormData();
      
      // Add lesson data
      Object.keys(lessonData).forEach(key => {
        if (lessonData[key] !== undefined && lessonData[key] !== null) {
          formData.append(key, lessonData[key]);
        }
      });
      
      // Add thumbnail if provided
      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }
      
      const response = await api.post('/direct-upload/upload-url', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'فشل الحصول على رابط الرفع');
    }
  }

  /**
   * Upload file using TUS Resumable Upload protocol
   * Benefits:
   * - Resume interrupted uploads
   * - Better progress tracking
   * - Automatic retries on network failures
   * - Works on mobile networks
   * 
   * @param {File} file - Video file
   * @param {Object} tusCredentials - From /upload-url response
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result
   */
  async uploadViaTUS(file, tusCredentials, onProgress = null) {
    return new Promise((resolve, reject) => {
      const { tusEndpoint, videoId, libraryId, signature, expirationTime } = tusCredentials;

      const upload = new tus.Upload(file, {
        endpoint: tusEndpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
        headers: {
          AuthorizationSignature: signature,
          AuthorizationExpire: expirationTime,
          VideoId: videoId,
          LibraryId: libraryId,
        },
        metadata: {
          filetype: file.type,
          title: file.name,
        },
        
        onError: function (error) {
          if (error.originalRequest) {
            reject(new Error(`Server error: ${error.message}`));
          } else {
            reject(new Error(`Upload error: ${error.message}`));
          }
        },
        
        onProgress: function (bytesUploaded, bytesTotal) {
          if (onProgress) {
            const percentComplete = (bytesUploaded / bytesTotal) * 100;
            onProgress(percentComplete);
          }
        },
        
        onSuccess: function () {
          resolve({ success: true, message: 'Upload complete!' });
        },
        
        onShouldRetry: function (error, retryAttempt, options) {
          // Retry on network errors or 5xx server errors
          const status = error.originalResponse?.getStatus?.();
          if (status >= 500 && status < 600) {
            return true;
          }
          return true; // Retry by default
        }
      });

      // Check for previous uploads and resume if available
      upload.findPreviousUploads().then(function (previousUploads) {
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      }).catch(function (error) {
        upload.start();
      });
    });
  }

  /**
   * Upload file directly to Bunny Stream (legacy method, not resumable)
   * @deprecated Use uploadViaTUS for better reliability
   * @param {File} file - Video file
   * @param {string} uploadUrl - Upload URL from backend (/upload-url response)
   * @param {string} accessKey - AccessKey from backend (/upload-url response)
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result
   */
  async uploadToBunnyDirect(file, uploadUrl, accessKey, onProgress = null) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseData = xhr.responseText ? JSON.parse(xhr.responseText) : { success: true };
            resolve({ success: true, data: responseData });
          } catch (parseError) {
            resolve({ success: true });
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timeout'));
      });

      // Configure and send request
      xhr.open('PUT', uploadUrl, true);
      xhr.timeout = 4 * 60 * 60 * 1000; // 4 hours timeout (for large 10GB files)
      
      // ✅ Only header needed per Bunny documentation
      xhr.setRequestHeader('AccessKey', accessKey);
      
      // ✅ Send file directly as binary, no JSON wrapping
      xhr.send(file);
    });
  }

  /**
   * Confirm upload completion with backend
   * Backend will: fetch video duration, update lesson status
   * 
   * @param {number} lessonId - Lesson ID
   * @param {string} videoId - Video ID
   * @returns {Promise<Object>} Confirmation result
   */
  async confirmUpload(lessonId, videoId) {
    try {
      const response = await api.post('/direct-upload/confirm', 
        { lessonId, videoId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'فشل تأكيد الرفع';
      throw new Error(errorMessage);
    }
  }

  async cancelUpload(lessonId) {
    try {
      const response = await api.delete(`/direct-upload/cancel/${lessonId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'فشل إلغاء الرفع';
      throw new Error(errorMessage);
    }
  }

  async completeDirectUpload(file, lessonData, thumbnailFile = null, onProgress = null) {
    let uploadInfo = null;
    
    try {
      uploadInfo = await this.getUploadUrl(lessonData, thumbnailFile);
      
      const videoId = uploadInfo.data.videoId;
      const lessonId = uploadInfo.data.lessonId;

      await this.uploadViaTUS(file, {
        tusEndpoint: uploadInfo.data.tusEndpoint,
        videoId: uploadInfo.data.videoId,
        libraryId: uploadInfo.data.libraryId,
        signature: uploadInfo.data.signature,
        expirationTime: uploadInfo.data.expirationTime
      }, onProgress);
      
      const confirmation = await this.confirmUpload(lessonId, videoId);
      
      return {
        success: true,
        lesson: confirmation.lesson,
        uploadInfo: uploadInfo.data
      };
      
    } catch (error) {
      if (uploadInfo?.data?.lessonId) {
        try {
          await this.cancelUpload(uploadInfo.data.lessonId);
        } catch (cleanupError) {
          // Cleanup failed
        }
      }
      throw error;
    }
  }

  /**
   * Complete direct upload flow (legacy - fallback to simple PUT)
   * @deprecated Use completeDirectUpload which uses TUS protocol
   */
  async completeDirectUploadLegacy(file, lessonData, thumbnailFile = null, onProgress = null) {
    let uploadInfo = null;
    
    try {
      // Step 1: Get upload URL and accessKey from backend
      uploadInfo = await this.getUploadUrl(lessonData, thumbnailFile);
      
      const videoId = uploadInfo.data.videoId;
      const lessonId = uploadInfo.data.lessonId;
      const uploadUrl = uploadInfo.data.uploadUrl;
      const accessKey = uploadInfo.data.accessKey;

      await this.uploadToBunnyDirect(file, uploadUrl, accessKey, onProgress);
      const confirmation = await this.confirmUpload(lessonId, videoId);
      
      return {
        success: true,
        lesson: confirmation.lesson,
        uploadInfo: uploadInfo.data
      };
      
    } catch (error) {
      // Cleanup on failure
      if (uploadInfo?.data?.lessonId) {
        try {
          await this.cancelUpload(uploadInfo.data.lessonId);
        } catch (cleanupError) {
          // Failed to cleanup after upload failure
        }
      }
      
      throw error;
    }
  }

  /**
   * Validate file before upload
   * @param {File} file - Video file
   * @returns {Object} Validation result
   */
  validateFile(file) {
    const maxSize = 10 * 1024 * 1024 * 1024; // 10GB
    const allowedTypes = [
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo'
    ];

    const errors = [];

    if (file.size > maxSize) {
      errors.push('حجم الملف يجب ألا يتجاوز 10 جيجابايت');
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push('نوع الملف غير مدعوم. يرجى استخدام MP4, WebM, MOV, أو AVI');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const directUploadService = new DirectUploadService();
export default directUploadService;
